import {
  CARD_PRIORITY_LABELS,
  ICON_LABELS,
  LIMITS,
  STATUS_LABELS,
  intentIsEmpty,
  updateCard,
  type Card,
  type CardPriority,
} from '@blueprint/domain'
import { InlineTitle } from '../../components/controls.js'
import { IconSvg } from '../../components/ItemFields.js'
import { useBoardStore } from '../../state/board-store.js'
import { formatMinutes } from '../../state/selectors.js'
import { useUiStore } from '../../state/ui-store.js'
import { ArchitectureIcons } from '../context/ArchitectureIcons.js'
import { FindingsBadge } from '../findings/Findings.js'
import { memo, type CSSProperties } from 'react'
import type { DraggableProvided } from '@hello-pangea/dnd'
import styles from './CardPreview.module.css'

export const CARD_SLOT_HEIGHT = 92

interface BodyProps {
  card: Card
  onMove: (cardId: string) => void
  onPrompt: (cardId: string) => void
}

/**
 * Fixed height, two-line title, compact metadata. Full content lives in the editor.
 * Memoized on the card object: a scroll-driven rerender of the list does not rebuild it.
 */
const CardBody = memo(function CardBody({ card, onMove, onPrompt }: BodyProps) {
  const estimate = formatMinutes(card.estimateMinutes)
  const hasIntent = !intentIsEmpty(card.intent)
  // Set by a quick insert, so this card opens with its title ready to be typed over.
  const focused = useUiStore((state) => state.focusCardId === card.id)
  return (
    <>
      {focused ? (
        <QuickTitle card={card} />
      ) : (
        <span className={styles.title}>
          <ArchitectureIcons context={card.context} />
          {card.icon ? (
            <span className={styles.icon} title={ICON_LABELS[card.icon]}>
              <IconSvg icon={card.icon} size={14} />
            </span>
          ) : null}
          {card.title}
        </span>
      )}
      <span className={styles.meta}>
        <span className={`${styles.status} ${styles[`status_${card.status}`] ?? ''}`}>
          {STATUS_LABELS[card.status]}
        </span>
        {card.priority ? <PriorityMark priority={card.priority} /> : null}
        {hasIntent ? <IntentMark /> : null}
        {card.owner ? <span className={styles.owner}>{card.owner}</span> : null}
        <span className={styles.spacer} />
        {estimate ? <span className={styles.estimate}>{estimate}</span> : null}
        <FindingsBadge targetId={card.id} label={card.title} />
        <span className={styles.version} title={`Version ${card.version ?? 1}`}>
          v{card.version ?? 1}
        </span>
        {/* Interactive elements do not start a drag, and the click never reaches the
            card's own open-editor handler. */}
        <button
          type="button"
          className={styles.move}
          aria-label={`Prompt preview for ${card.title}`}
          title="Prompt preview"
          data-testid="card-prompt"
          onClick={(event) => {
            event.stopPropagation()
            onPrompt(card.id)
          }}
          onKeyDown={(event) => event.stopPropagation()}
        >
          &#10095;
        </button>
        <button
          type="button"
          className={styles.move}
          aria-label={`Move ${card.title}`}
          title="Move to another row or column"
          onClick={(event) => {
            event.stopPropagation()
            onMove(card.id)
          }}
          onKeyDown={(event) => event.stopPropagation()}
        >
          &#8942;
        </button>
      </span>
      <span className="visually-hidden">
        {hasIntent ? 'Has AI context. ' : ''}
        {card.release ? `Release ${card.release}. ` : ''}
        {card.tags.length > 0 ? `Tags: ${card.tags.join(', ')}. ` : ''}
        {card.dueDate ? `Due ${card.dueDate}.` : ''}
      </span>
    </>
  )
})

/**
 * The title of a just-inserted card, open for renaming in place. The wrapper stops the
 * click and keydown reaching the card, which would otherwise open the editor mid-edit —
 * the same guard the move and prompt buttons use.
 */
function QuickTitle({ card }: { card: Card }) {
  const setFocusCardId = useUiStore((state) => state.setFocusCardId)
  return (
    <span
      className={styles.title}
      onClick={(event) => event.stopPropagation()}
      onKeyDown={(event) => event.stopPropagation()}
    >
      <InlineTitle
        autoEdit
        value={card.title}
        label={`Name card ${card.title}`}
        maxLength={LIMITS.cardTitle}
        onCommit={(title) => useBoardStore.getState().run((state) => updateCard(state, card.id, { title }))}
        onEditEnd={() => setFocusCardId(null)}
      />
    </span>
  )
}

/**
 * A flag coloured by priority. Colour alone would not be readable by everyone, so the
 * level is also given as a tooltip and as text only screen readers see.
 */
function PriorityMark({ priority }: { priority: CardPriority }) {
  const label = `${CARD_PRIORITY_LABELS[priority]} priority`
  return (
    <span className={`${styles.priority} ${styles[`priority_${priority}`] ?? ''}`} title={label}>
      <svg viewBox="0 0 24 24" aria-hidden="true">
        <path d="M6.2 2.8v18.4" stroke="currentColor" strokeWidth="2.6" strokeLinecap="round" fill="none" />
        <path d="M8 4h11l-2.7 4.4L19 12.8H8Z" fill="currentColor" />
      </svg>
      <span className="visually-hidden">{label}</span>
    </span>
  )
}

/**
 * Marks a card whose AI context has been filled in. Decorative here: the same fact is
 * announced from the card's visually-hidden summary, so screen readers hear it once.
 */
function IntentMark() {
  return (
    <span className={styles.intentMark} title="AI context set">
      <svg width="12" height="12" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
        <path d="M12 2.5 13.7 8 19 9.8 13.7 11.5 12 17 10.3 11.5 5 9.8 10.3 8 12 2.5Z" />
        <path d="M18.5 14.5 19.4 17l2.6.9-2.6.9-.9 2.6-.9-2.6-2.6-.9 2.6-.9.9-2.5Z" />
      </svg>
    </span>
  )
}

interface CardPreviewProps {
  card: Card
  provided: DraggableProvided
  isDragging: boolean
  dragDisabled: boolean
  /** Absolute placement supplied by the virtualizer; omitted for the drag clone. */
  style?: CSSProperties
  onOpen: (cardId: string) => void
  onMove: (cardId: string) => void
  onPrompt: (cardId: string) => void
}

export function CardPreview({
  card,
  provided,
  isDragging,
  dragDisabled,
  style,
  onOpen,
  onMove,
  onPrompt,
}: CardPreviewProps) {
  const colourClass = card.colour === 'default' ? '' : (styles[card.colour] ?? '')
  return (
    <div
      ref={provided.innerRef}
      {...provided.draggableProps}
      {...provided.dragHandleProps}
      // Virtual placement first, then the library's own style: while dragging, the
      // library's position/transform must win (the reference virtual-list pattern).
      style={{ ...style, ...provided.draggableProps.style }}
      className={styles.slot}
      data-card-id={card.id}
      data-testid="card"
      onClick={() => onOpen(card.id)}
      onKeyDown={(event) => {
        if (event.key === 'Enter') {
          event.preventDefault()
          onOpen(card.id)
        }
      }}
    >
      <article
        className={`${styles.card} ${colourClass} ${isDragging ? styles.dragging : ''} ${
          dragDisabled ? styles.locked : ''
        }`}
      >
        <CardBody card={card} onMove={onMove} onPrompt={onPrompt} />
      </article>
    </div>
  )
}
